<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { tick } from 'svelte';
  import { profileActions, profileStore } from '@/stores/game/profile';
  import { itemsDB } from '@/game/database';
  import { animations } from '@/utils/animations';
  import { panelPositioningService } from '@/engine/ui';
  import { getItemIconUrl } from '@/assets/icons';

  let panelElement: HTMLElement;

  // Reactive statement to get current resources from store
  $: currentResources = Object.values($profileStore.inventory.items);
  $: creditsItem = currentResources.find(item => item.itemId === 'credits');

  // Debug logging
  $: if (creditsItem) {
    console.log('🔍 ResourcePanel creditsItem:', creditsItem);
    console.log('🔍 ResourcePanel getResourceIcon result:', getResourceIcon(creditsItem.itemId));
  } else {
    console.log('🔍 ResourcePanel creditsItem is undefined');
    console.log('🔍 ResourcePanel currentResources:', currentResources);
  }

  onMount(() => {
    if (panelElement) {
      // Регистрируем панель в сервисе позиционирования
      panelPositioningService.registerPanel({
        id: 'resource-panel',
        anchor: 'TL',
        offsetX: 16, // 4 * 4px = 16px (top-4)
        offsetY: 0, // 4 * 4px = 16px (left-4)
        element: panelElement,
      });

      // Анимируем появление
      animations.panelSlideIn(panelElement, 'left');

      if (panelPositioningService.hasPanel('resource-panel')) {
        void tick().then(() => {
          panelPositioningService.refreshPanel('resource-panel');
        });
      }
    }
  });

  onDestroy(() => {
    panelPositioningService.unregisterPanel('resource-panel');
  });

  // Dev function to add coins (for testing)
  function handleCoinClick(event: MouseEvent) {
    event.stopPropagation();
    profileActions.addInventoryItem('credits', 10);
    const target = event.currentTarget as HTMLElement;
    if (target) {
      animations.resourceGain(target);
    }
  }

  // Format numbers for display
  function formatNumber(num: number): string {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  }

  // Get resource icon URL - используем правильный Vite импорт
  function getResourceIcon(itemId: string): string {
    const iconPath = itemsDB.getItemIcon(itemId);
    return getItemIconUrl(iconPath);
  }
</script>

<div bind:this={panelElement} class="resource-panel">
  {#if creditsItem}
    <button
      class="resource-item coins"
      on:click={handleCoinClick}
      type="button"
      title="Credits"
      data-resource-type="credits"
    >
      <div class="resource-icon">
        <img src={getResourceIcon('credits')} alt="credits" class="resource-icon-svg" />
      </div>
      <div class="resource-amount">
        ${formatNumber(creditsItem.quantity)}
      </div>
    </button>
  {:else}
    <!-- Fallback when no credits -->
    <div class="resource-item resource-empty">
      <div class="resource-icon">💰</div>
      <div class="resource-amount">$0</div>
    </div>
  {/if}
</div>

<style lang="postcss">
  @reference "@/styles/theme.css";

  .resource-panel {
    @apply z-50;
    @apply flex flex-col gap-1;
    @apply pointer-events-auto;
    @apply min-h-[40px]; /* Минимальная высота для видимости */
    /* Позиционирование теперь управляется через PanelPositioningService */
    /* Убираем фон и обводку - каждый ресурс теперь в своем блоке */
  }

  .resource-item {
    @apply flex items-center gap-1;
    @apply cursor-pointer transition-all duration-200;
    @apply hover:bg-neon-green/10 rounded-md;
    @apply px-1 py-1 min-w-[60px];
    @apply bg-black/80 backdrop-blur-sm border border-neon-green/40;
    @apply relative overflow-hidden;
  }

  .resource-item::before {
    content: '';
    @apply absolute inset-0 bg-gradient-to-r from-neon-green/5 to-transparent;
    @apply opacity-0 transition-opacity duration-200;
  }

  .resource-item:hover::before {
    @apply opacity-100;
  }

  .resource-icon {
    @apply w-5 h-5 flex items-center justify-center;
    @apply flex-shrink-0;
  }

  .resource-icon-svg {
    @apply w-4 h-4 object-contain;
    filter: drop-shadow(0 0 3px rgba(0, 255, 255, 0.4));
  }

  .resource-amount {
    @apply text-neon-green font-mono text-xs font-bold;
    @apply text-left flex-shrink-0;
    @apply min-w-[40px];
  }

  .resource-item.coins {
    @apply border-neon-yellow/40;
    @apply hover:bg-neon-yellow/10;
  }

  .resource-item.coins .resource-amount {
    @apply text-neon-yellow;
  }

  .resource-empty {
    @apply opacity-50;
    @apply cursor-default;
  }

  .resource-empty:hover {
    @apply bg-neon-green/5;
  }
</style>
